import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import Navbar from "../components/Navbar";
import { ArrowLeft, BedDouble, Bath, Square, MapPin, X, Eye } from "lucide-react";
import { Button } from "../components/ui/button";

const formatPrice = (price, listingType) => {
  if (listingType === "rent") return `K${price?.toLocaleString()}/mo`;
  return `K${price?.toLocaleString()}`;
};

const ComparePropertiesPage = () => {
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = searchParams.get("ids")?.split(",").filter(Boolean) || [];
    if (ids.length >= 2) fetchProperties(ids);
    else setLoading(false);
  }, [searchParams]);

  const fetchProperties = async (ids) => {
    setLoading(true);
    try {
      const results = await Promise.all(
        ids.map((id) => axios.get(`${API}/properties/${id}`).then((r) => r.data).catch(() => null))
      );
      setProperties(results.filter(Boolean));
    } catch (e) {
      console.error("Error fetching properties:", e);
    } finally {
      setLoading(false);
    }
  };

  const removeProperty = (id) => {
    const remaining = properties.filter((p) => p.property_id !== id);
    if (remaining.length < 2) {
      window.history.back();
      return;
    }
    setProperties(remaining);
    const newIds = remaining.map((p) => p.property_id).join(",");
    window.history.replaceState(null, "", `/properties/compare?ids=${newIds}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (properties.length < 2) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-serif mb-4">Not Enough Properties</h2>
          <p className="text-muted-foreground mb-6">Select at least 2 properties to compare.</p>
          <Button asChild>
            <Link to="/properties"><ArrowLeft className="w-4 h-4 mr-2" /> Back to Properties</Link>
          </Button>
        </div>
      </div>
    );
  }

  const colCount = properties.length;

  const rows = [
    { label: "Price", render: (p) => <span className="font-mono text-lg font-bold text-primary">{formatPrice(p.price, p.listing_type)}</span> },
    { label: "Status", render: (p) => (
      <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${
        p.status === "active" ? "bg-green-100 text-green-700" :
        p.status === "rented" ? "bg-orange-100 text-orange-700" :
        p.status === "sold" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
      }`}>{p.status}</span>
    )},
    { label: "Type", render: (p) => <span className="capitalize">{p.property_type}</span> },
    { label: "Listing", render: (p) => <span>{p.listing_type === "rent" ? "For Rent" : "For Sale"}</span> },
    { label: "Bedrooms", render: (p) => <span className="flex items-center gap-1.5"><BedDouble className="w-4 h-4 text-muted-foreground" /> {p.bedrooms}</span> },
    { label: "Bathrooms", render: (p) => <span className="flex items-center gap-1.5"><Bath className="w-4 h-4 text-muted-foreground" /> {p.bathrooms}</span> },
    { label: "Area", render: (p) => <span className="flex items-center gap-1.5"><Square className="w-4 h-4 text-muted-foreground" /> {p.area_sqft?.toLocaleString()} sqft</span> },
    { label: "Price/sqft", render: (p) => <span className="font-mono">K{p.area_sqft ? Math.round(p.price / p.area_sqft).toLocaleString() : "-"}</span> },
    { label: "Location", render: (p) => <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-muted-foreground" /> {p.city}, {p.state}</span> },
    { label: "Address", render: (p) => <span className="text-sm">{p.address}</span> },
    { label: "Views", render: (p) => <span className="flex items-center gap-1.5"><Eye className="w-4 h-4 text-muted-foreground" /> {p.views}</span> },
    { label: "Features", render: (p) => (
      <div className="flex flex-wrap gap-1">
        {(p.features || []).length > 0
          ? p.features.map((f) => <span key={f} className="text-xs bg-muted px-2 py-0.5 rounded">{f}</span>)
          : <span className="text-muted-foreground text-sm">-</span>
        }
      </div>
    )},
    { label: "Ref #", render: (p) => <span className="font-mono text-sm">{p.ref_number}</span> },
  ];

  // Find best values for highlighting
  const bestPrice = Math.min(...properties.map((p) => p.price));
  const bestArea = Math.max(...properties.map((p) => p.area_sqft || 0));
  const bestPricePerSqft = Math.min(...properties.map((p) => p.area_sqft ? Math.round(p.price / p.area_sqft) : Infinity));

  return (
    <div className="min-h-screen bg-background" data-testid="compare-properties-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="sm" asChild>
            <Link to="/properties"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
          </Button>
          <div>
            <h1 className="text-2xl font-serif">Compare Properties</h1>
            <p className="text-muted-foreground text-sm">Comparing {colCount} properties side by side</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              {/* Property images & titles */}
              <thead>
                <tr className="border-b">
                  <th className="p-4 text-left text-sm font-medium text-muted-foreground w-36 align-top bg-muted/30">Property</th>
                  {properties.map((p) => (
                    <th key={p.property_id} className="p-4 text-left align-top min-w-[220px]">
                      <div className="relative">
                        <button
                          onClick={() => removeProperty(p.property_id)}
                          className="absolute -top-1 -right-1 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center hover:bg-destructive/80 z-10"
                          data-testid={`remove-compare-${p.property_id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <Link to={`/properties/${p.property_id}`}>
                          <img
                            src={p.images?.[0] || "https://images.unsplash.com/photo-1746458258536-b9ee5db20a73"}
                            alt={p.title}
                            className="w-full h-36 object-cover rounded-sm mb-2"
                          />
                          <h3 className="font-serif text-base leading-tight hover:text-primary transition-colors line-clamp-2">{p.title}</h3>
                        </Link>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? "bg-muted/20" : ""}>
                    <td className="p-4 text-sm font-medium text-muted-foreground bg-muted/30 border-r">{row.label}</td>
                    {properties.map((p) => {
                      // Highlight best values
                      let highlight = false;
                      if (row.label === "Price" && p.price === bestPrice && colCount > 1) highlight = true;
                      if (row.label === "Area" && p.area_sqft === bestArea && colCount > 1) highlight = true;
                      if (row.label === "Price/sqft" && p.area_sqft && Math.round(p.price / p.area_sqft) === bestPricePerSqft && colCount > 1) highlight = true;

                      return (
                        <td key={p.property_id} className={`p-4 ${highlight ? "bg-green-50" : ""}`}>
                          {row.render(p)}
                          {highlight && <span className="block text-[10px] text-green-600 font-medium mt-0.5">Best value</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparePropertiesPage;
